import OpenInNew from '@mui/icons-material/OpenInNew'
import SwapHoriz from '@mui/icons-material/SwapHoriz'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { getUserRoles } from 'src/mentor/layout/utils'
import { getAvailablePortals } from 'src/shared/config/portalConfig'

const FLOW_BY_PORTAL_ID = {
  mentor: 'mentor',
  clinic: 'clinic',
  'clinic-assistant': 'clinic-assistant',
  kiosk: 'kiosk',
  support: 'support',
  business: 'business',
  patient: 'patient',
  'patient-prospect': 'patient-prospect',
}

function isCurrentPortal(portal) {
  if (portal.external) return false

  const currentPath = window.location.pathname

  if (portal.path === '/') {
    return (
      currentPath === '/' ||
      currentPath.startsWith('/wellness') ||
      (!currentPath.startsWith('/support-home') &&
        !currentPath.startsWith('/mentor-home') &&
        !currentPath.startsWith('/clinic-home') &&
        !currentPath.startsWith('/ca-home') &&
        !currentPath.startsWith('/kiosk') &&
        !currentPath.startsWith('/workplace') &&
        !currentPath.startsWith('/patient-home') &&
        !currentPath.startsWith('/care/status'))
    )
  }

  return currentPath === portal.path || currentPath.startsWith(`${portal.path}/`)
}

function rememberPortalSwitch(portal) {
  try {
    sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')

    if (portal.path === '/') {
      sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
      sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
      localStorage.removeItem('lastChosenFlow')
      return
    }

    const flow = FLOW_BY_PORTAL_ID[portal.id]
    if (flow) {
      localStorage.setItem('lastChosenFlow', flow)
    }
  } catch {
    // Storage can be blocked in private browsing; navigation should still work.
  }
}

export default function PortalSwitchGateCard({ compact = false }) {
  const navigate = useNavigate()
  const userRoles = getUserRoles()

  const availablePortals = useMemo(() => {
    if (!Array.isArray(userRoles)) return []
    return getAvailablePortals(userRoles)
  }, [userRoles])

  const switchTargets = availablePortals.filter((portal) => !isCurrentPortal(portal))

  if (availablePortals.length <= 1 || switchTargets.length === 0) {
    return null
  }

  const handleSwitch = (portal) => {
    rememberPortalSwitch(portal)

    if (portal.external) {
      window.open(portal.path, '_blank', 'noopener,noreferrer')
      return
    }

    if (portal.path === '/') {
      window.location.replace('/')
      return
    }

    navigate(portal.path)
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: compact ? 480 : 560,
        p: compact ? 1.5 : { xs: 2, sm: 2.5 },
        borderRadius: '14px',
        border: '1px solid rgba(37, 99, 235, 0.22)',
        background:
          'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(255, 255, 255, 0.98) 60%)',
        boxShadow: compact ? 'none' : '0 12px 32px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Stack spacing={compact ? 1 : 1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SwapHoriz sx={{ color: '#2563eb', fontSize: compact ? 20 : 22 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={compact ? 'body2' : 'subtitle1'}
              sx={{ fontWeight: 800, color: '#1e3151' }}
            >
              Switch to another portal
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: '#475569', lineHeight: 1.45 }}
            >
              This KYC gate only affects the mentor portal. Your other roles are still available.
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          useFlexGap
          flexWrap="wrap"
        >
          {switchTargets.map((portal) => {
            const Icon = portal.icon
            return (
              <Button
                key={portal.id}
                variant="contained"
                startIcon={<Icon />}
                endIcon={portal.external ? <OpenInNew /> : null}
                onClick={() => handleSwitch(portal)}
                sx={{
                  flex: compact ? '1 1 auto' : '1 1 160px',
                  justifyContent: 'flex-start',
                  minHeight: 42,
                  textTransform: 'none',
                  borderRadius: '10px',
                  backgroundColor: '#1e3151',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#17253d',
                    boxShadow: '0 8px 18px rgba(30, 49, 81, 0.18)',
                  },
                }}
              >
                {portal.name}
              </Button>
            )
          })}
        </Stack>
      </Stack>
    </Box>
  )
}
